import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface Workspace {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  role: string;
  member_count: number;
  pending_tasks: number;
  created_at: string;
}

interface Member {
  id: string;
  user_id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  joined_at: string;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expires_at: string;
}

interface Reaction {
  user_id: string;
  reaction: string;
  name?: string;
}

interface Task {
  id: string;
  title: string;
  description: string;
  scheduled_date: string;
  completion_percentage: number;
  creator_name: string;
  creator_first_name: string;
  user_id?: string;
  assigned_to_user_id?: string | null;
  assigned_to_name?: string | null;
  assigned_to_first_name?: string | null;
  reactions?: Reaction[];
}

interface Activity {
  id: string;
  activity_type: string;
  description: string;
  username: string;
  first_name: string;
  created_at: string;
}

const CollaborativeWorkspaces: React.FC = () => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [userRole, setUserRole] = useState<string>('member');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [inviteEmailSent, setInviteEmailSent] = useState(false);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [editTaskId, setEditTaskId] = useState<string | null>(null);
  const [editTaskForm, setEditTaskForm] = useState({ title: '', description: '', scheduledDate: '' });
  const currentUserId = apiService.getCurrentUser()?.id;

  const REACTIONS: { key: string; emoji: string; label: string }[] = [
    { key: 'clap', emoji: '👏', label: 'Well done' },
    { key: 'celebrate', emoji: '🎉', label: 'Celebrate' },
    { key: 'love', emoji: '❤️', label: 'Love it' },
    { key: 'thumbsup', emoji: '👍', label: 'Nice' },
    { key: 'important', emoji: '🔥', label: 'Important' }
  ];

  // Form states
  const [newWorkspace, setNewWorkspace] = useState({ name: '', description: '', color: '#667eea', icon: '👨‍👩‍👧' });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [newTask, setNewTask] = useState<{ title: string; description: string; scheduledDate: string; assignedTo: string }>({ title: '', description: '', scheduledDate: new Date().toISOString().split('T')[0], assignedTo: '' });

  const fetchWorkspaces = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getWorkspaces();
      if (response.success) {
        setWorkspaces(response.workspaces || []);
      }
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWorkspaceDetails = useCallback(async (workspaceId: string) => {
    try {
      const [detailsRes, tasksRes, activityRes] = await Promise.all([
        apiService.getWorkspaceDetails(workspaceId),
        apiService.getWorkspaceTasks(workspaceId, undefined, true),
        apiService.getWorkspaceActivity(workspaceId, 30)
      ]);

      if (detailsRes.success) {
        setSelectedWorkspace(detailsRes.workspace);
        setMembers(detailsRes.members || []);
        setPendingInvitations(detailsRes.pendingInvitations || []);
        setUserRole(detailsRes.userRole || 'member');
      }
      if (tasksRes.success) setTasks(tasksRes.tasks || []);
      if (activityRes.success) setActivity(activityRes.activity || []);
    } catch (error) {
      console.error('Failed to fetch workspace details:', error);
    }
  }, []);

  useEffect(() => { fetchWorkspaces(); }, [fetchWorkspaces]);

  const handleCreateWorkspace = async () => {
    if (!newWorkspace.name.trim()) return;
    try {
      const response = await apiService.createWorkspace(newWorkspace);
      if (response.success) {
        setWorkspaces([response.workspace, ...workspaces]);
        setShowCreateModal(false);
        setNewWorkspace({ name: '', description: '', color: '#667eea', icon: '👨‍👩‍👧' });
        // Auto-select the new workspace
        fetchWorkspaceDetails(response.workspace.id);
      }
    } catch (error) {
      console.error('Failed to create workspace:', error);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim() || !selectedWorkspace) return;
    try {
      const response = await apiService.inviteToWorkspace(selectedWorkspace.id, inviteEmail, inviteRole);
      if (response.success) {
        setPendingInvitations([...pendingInvitations, response.invitation]);
        setShowInviteModal(false);
        setInviteEmail('');
        
        // Copy invite link as a fallback and reflect email delivery status
        const inviteLink = `${window.location.origin}${response.inviteLink}`;
        navigator.clipboard.writeText(inviteLink).catch(() => {});
        setInviteEmailSent(!!response.emailSent);
        setCopiedLink(inviteLink);
        setTimeout(() => { setCopiedLink(null); setInviteEmailSent(false); }, 6000);
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to send invitation');
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim() || !selectedWorkspace) return;
    try {
      const response = await apiService.createWorkspaceTask(selectedWorkspace.id, {
        title: newTask.title,
        description: newTask.description,
        scheduledDate: newTask.scheduledDate,
        assignedTo: newTask.assignedTo || undefined
      });
      if (response.success) {
        setTasks([response.task, ...tasks]);
        setShowTaskModal(false);
        setNewTask({ title: '', description: '', scheduledDate: new Date().toISOString().split('T')[0], assignedTo: '' });
      }
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const handleAssignTask = async (taskId: string, assignedTo: string) => {
    if (!selectedWorkspace) return;
    try {
      const response = await apiService.assignWorkspaceTask(selectedWorkspace.id, taskId, assignedTo || null);
      if (response.success) {
        setTasks(tasks.map(t => t.id === taskId ? { ...t, ...response.task } : t));
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to assign task');
    }
  };

  const handleToggleComplete = async (task: Task) => {
    if (!selectedWorkspace) return;
    const completed = task.completion_percentage < 100;
    try {
      const response = await apiService.completeWorkspaceTask(selectedWorkspace.id, task.id, completed);
      if (response.success) {
        setTasks(tasks.map(t => t.id === task.id ? { ...t, ...response.task } : t));
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to update task');
    }
  };

  const startEditTask = (task: Task) => {
    setEditTaskId(task.id);
    setEditTaskForm({
      title: task.title,
      description: task.description || '',
      scheduledDate: (task.scheduled_date || '').split('T')[0]
    });
  };

  const cancelEditTask = () => setEditTaskId(null);

  const saveEditTask = async (task: Task) => {
    if (!selectedWorkspace || !editTaskForm.title.trim()) return;
    try {
      const response = await apiService.editWorkspaceTask(selectedWorkspace.id, task.id, {
        title: editTaskForm.title.trim(),
        description: editTaskForm.description,
        scheduledDate: editTaskForm.scheduledDate || undefined
      });
      if (response.success) {
        setTasks(tasks.map(t => t.id === task.id ? { ...t, ...response.task } : t));
        setEditTaskId(null);
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to edit task');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!selectedWorkspace || !window.confirm('Delete this task for everyone in the group?')) return;
    try {
      await apiService.deleteWorkspaceTask(selectedWorkspace.id, taskId);
      setTasks(tasks.filter(t => t.id !== taskId));
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete task');
    }
  };

  const handleReact = async (taskId: string, reaction: string) => {
    if (!selectedWorkspace) return;
    try {
      const response = await apiService.reactToWorkspaceTask(selectedWorkspace.id, taskId, reaction);
      if (response.success) {
        setTasks(tasks.map(t => t.id === taskId ? { ...t, reactions: response.reactions } : t));
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to react');
    }
  };

  const canManageTask = (task: Task) =>
    ['owner', 'admin'].includes(userRole) || task.user_id === currentUserId;

  const renderTaskCard = (task: Task) => {
    const done = task.completion_percentage >= 100;
    const creatorName = task.creator_first_name || task.creator_name || 'Someone';
    const reactions = task.reactions || [];
    const counts: Record<string, number> = {};
    const mine = new Set<string>();
    reactions.forEach(r => {
      counts[r.reaction] = (counts[r.reaction] || 0) + 1;
      if (r.user_id === currentUserId) mine.add(r.reaction);
    });
    const accent = done ? '#22c55e' : '#667eea';

    if (editTaskId === task.id) {
      return (
        <div
          key={task.id}
          data-testid={`shared-task-editor-${task.id}`}
          style={{
            padding: '14px 16px',
            background: 'white',
            borderRadius: '14px',
            borderLeft: '5px solid #f59e0b',
            boxShadow: '0 6px 18px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}
        >
          <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.05em' }}>✏️ Editing task</span>
          <input
            type="text"
            value={editTaskForm.title}
            onChange={e => setEditTaskForm({ ...editTaskForm, title: e.target.value })}
            onKeyDown={e => { if (e.key === 'Enter') saveEditTask(task); if (e.key === 'Escape') cancelEditTask(); }}
            autoFocus
            placeholder="Task title"
            data-testid={`edit-shared-title-${task.id}`}
            style={{ padding: '8px 12px', border: '2px solid #f59e0b', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 600, outline: 'none', boxSizing: 'border-box', width: '100%' }}
          />
          <textarea
            value={editTaskForm.description}
            onChange={e => setEditTaskForm({ ...editTaskForm, description: e.target.value })}
            onKeyDown={e => { if (e.key === 'Escape') cancelEditTask(); }}
            placeholder="Notes (optional)"
            rows={2}
            data-testid={`edit-shared-notes-${task.id}`}
            style={{ padding: '8px 12px', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '0.82rem', outline: 'none', resize: 'vertical', fontFamily: 'inherit', color: '#374151', boxSizing: 'border-box', width: '100%' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280' }}>
              📅 Due
              <input
                type="date"
                value={editTaskForm.scheduledDate}
                onChange={e => setEditTaskForm({ ...editTaskForm, scheduledDate: e.target.value })}
                data-testid={`edit-shared-date-${task.id}`}
                style={{ padding: '4px 8px', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '0.75rem', outline: 'none', color: '#374151' }}
              />
            </label>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              <button
                onClick={() => saveEditTask(task)}
                disabled={!editTaskForm.title.trim()}
                data-testid={`save-shared-edit-${task.id}`}
                style={{ padding: '6px 16px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', opacity: editTaskForm.title.trim() ? 1 : 0.5 }}
              >
                Save
              </button>
              <button
                onClick={cancelEditTask}
                data-testid={`cancel-shared-edit-${task.id}`}
                style={{ padding: '6px 16px', backgroundColor: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '8px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={task.id}
        data-testid={`shared-task-${task.id}`}
        style={{
          position: 'relative',
          padding: '14px 16px',
          background: done
            ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
            : 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%)',
          borderRadius: '14px',
          borderLeft: `5px solid ${accent}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <button
            onClick={() => handleToggleComplete(task)}
            data-testid={`toggle-complete-${task.id}`}
            title={done ? 'Mark as pending' : 'Mark as done'}
            style={{
              flexShrink: 0, width: '26px', height: '26px', borderRadius: '50%',
              border: `2px solid ${done ? '#22c55e' : '#c7d2fe'}`,
              backgroundColor: done ? '#22c55e' : 'white',
              color: 'white', cursor: 'pointer', fontSize: '0.8rem', lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px'
            }}
          >
            {done ? '✓' : ''}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1rem', fontWeight: 600, textDecoration: done ? 'line-through' : 'none', color: done ? '#15803d' : '#111827' }}>
                {done ? '✅' : '📌'} {task.title}
              </span>
              <span style={{
                fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '10px',
                backgroundColor: done ? '#bbf7d0' : '#e0e7ff', color: done ? '#166534' : '#4338ca'
              }}>
                {done ? 'DONE' : 'PENDING'}
              </span>
            </div>
            {task.description && (
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '2px' }}>{task.description}</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px', flexWrap: 'wrap', fontSize: '0.72rem', color: '#6b7280' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <span style={{
                  width: '18px', height: '18px', borderRadius: '50%', backgroundColor: accent, color: 'white',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700
                }}>{creatorName[0]?.toUpperCase()}</span>
                by {creatorName}
              </span>
              <span>📅 {formatDate(task.scheduled_date)}</span>
              {task.assigned_to_user_id && (
                <span style={{ padding: '2px 8px', backgroundColor: '#dbeafe', color: '#2563eb', borderRadius: '10px', fontWeight: 600 }}>
                  👤 {task.assigned_to_first_name || task.assigned_to_name}
                </span>
              )}
            </div>

            {/* Reactions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '10px', flexWrap: 'wrap' }}>
              {REACTIONS.map(r => {
                const count = counts[r.key] || 0;
                const reacted = mine.has(r.key);
                return (
                  <button
                    key={r.key}
                    onClick={() => handleReact(task.id, r.key)}
                    data-testid={`react-${r.key}-${task.id}`}
                    title={r.label}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '3px',
                      padding: '2px 8px', borderRadius: '12px', cursor: 'pointer',
                      border: reacted ? '1.5px solid #f59e0b' : '1px solid #e5e7eb',
                      backgroundColor: reacted ? '#fffbeb' : 'white',
                      fontSize: '0.8rem', transition: 'all 0.15s'
                    }}
                  >
                    <span>{r.emoji}</span>
                    {count > 0 && <span style={{ fontSize: '0.7rem', fontWeight: 700, color: reacted ? '#b45309' : '#6b7280' }}>{count}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Manage controls */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
            {canManageTask(task) && (
              <div style={{ display: 'flex', gap: '4px' }}>
                <button onClick={() => startEditTask(task)} data-testid={`edit-task-${task.id}`} title="Edit" style={{ background: '#ffffffcc', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem' }}>✏️</button>
                <button onClick={() => handleDeleteTask(task.id)} data-testid={`delete-task-${task.id}`} title="Delete" style={{ background: '#ffffffcc', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem' }}>🗑️</button>
              </div>
            )}
            {['owner', 'admin'].includes(userRole) && (
              <select
                value={task.assigned_to_user_id || ''}
                onChange={e => handleAssignTask(task.id, e.target.value)}
                data-testid={`assign-task-select-${task.id}`}
                style={{ fontSize: '0.7rem', padding: '2px 4px', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#6b7280', cursor: 'pointer', maxWidth: '120px' }}
              >
                <option value="">Unassigned</option>
                {members.map(m => (
                  <option key={m.user_id} value={m.user_id}>{m.first_name || m.username}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!selectedWorkspace || !confirm('Remove this member from the workspace?')) return;
    try {
      await apiService.removeWorkspaceMember(selectedWorkspace.id, memberId);
      setMembers(members.filter(m => m.id !== memberId));
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to remove member');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await apiService.cancelInvitation(invitationId);
      setPendingInvitations(pendingInvitations.filter(i => i.id !== invitationId));
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const iconOptions = ['👨‍👩‍👧', '👨‍👩‍👧‍👦', '🏠', '💼', '🎉', '📋', '🎯', '⭐', '💪', '🌟'];
  const colorOptions = ['#667eea', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading workspaces...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0 }}>Collaborative Workspaces</h1>
          <p style={{ color: '#6b7280', marginTop: '4px' }}>Share tasks with family members and collaborate together</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          data-testid="create-workspace-btn"
          style={{
            padding: '10px 20px',
            backgroundColor: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          + Create Workspace
        </button>
      </div>

      {/* Copied Link Notification */}
      {copiedLink && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#dcfce7',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>✅</span>
          <span>
            {inviteEmailSent
              ? 'Invitation email sent! The link was also copied as a backup.'
              : 'Invite link copied! Share it with your family member.'}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '24px' }}>
        {/* Workspace List */}
        <div style={{ width: '300px', flexShrink: 0 }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#6b7280', marginBottom: '12px' }}>Your Workspaces</h3>
          {workspaces.length === 0 ? (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              border: '2px dashed #e5e7eb'
            }}>
              <p style={{ color: '#6b7280', marginBottom: '16px' }}>No workspaces yet</p>
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Create Your First Workspace
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {workspaces.map(ws => (
                <div
                  key={ws.id}
                  onClick={() => fetchWorkspaceDetails(ws.id)}
                  data-testid={`workspace-item-${ws.id}`}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: selectedWorkspace?.id === ws.id ? '#f0f4ff' : 'white',
                    border: `2px solid ${selectedWorkspace?.id === ws.id ? '#667eea' : '#e5e7eb'}`,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '1.5rem' }}>{ws.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>{ws.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {ws.member_count} member{ws.member_count !== 1 ? 's' : ''} · {ws.pending_tasks} tasks
                      </div>
                    </div>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: ws.color
                    }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Workspace Details */}
        {selectedWorkspace ? (
          <div style={{ flex: 1 }}>
            {/* Workspace Header */}
            <div style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <span style={{ fontSize: '2.5rem' }}>{selectedWorkspace.icon}</span>
                  <div>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{selectedWorkspace.name}</h2>
                    {selectedWorkspace.description && (
                      <p style={{ color: '#6b7280', marginTop: '4px' }}>{selectedWorkspace.description}</p>
                    )}
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '4px' }}>
                      Your role: <strong>{userRole}</strong>
                    </div>
                  </div>
                </div>
                {['owner', 'admin'].includes(userRole) && (
                  <button
                    onClick={() => setShowInviteModal(true)}
                    data-testid="invite-member-btn"
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#22c55e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    + Invite Member
                  </button>
                )}
              </div>
            </div>

            {/* Members */}
            <div style={{
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              marginBottom: '16px'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '12px' }}>Members ({members.length})</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {members.map(member => (
                  <div
                    key={member.id}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '0.875rem'
                    }}
                  >
                    <span style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: '#667eea',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}>
                      {(member.first_name || member.username || member.email)[0].toUpperCase()}
                    </span>
                    <span>{member.first_name || member.username || member.email.split('@')[0]}</span>
                    <span style={{
                      fontSize: '0.65rem',
                      padding: '2px 6px',
                      backgroundColor: member.role === 'owner' ? '#fef3c7' : '#e5e7eb',
                      borderRadius: '4px',
                      color: member.role === 'owner' ? '#92400e' : '#6b7280'
                    }}>
                      {member.role}
                    </span>
                    {['owner', 'admin'].includes(userRole) && member.role !== 'owner' && (
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '2px'
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Pending Invitations */}
              {pendingInvitations.length > 0 && (
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                  <h4 style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '8px' }}>Pending Invitations</h4>
                  {pendingInvitations.map(inv => (
                    <div key={inv.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px',
                      backgroundColor: '#fefce8',
                      borderRadius: '6px',
                      marginBottom: '4px'
                    }}>
                      <span style={{ fontSize: '0.875rem' }}>{inv.email}</span>
                      <button
                        onClick={() => handleCancelInvitation(inv.id)}
                        style={{
                          fontSize: '0.75rem',
                          color: '#ef4444',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tasks Section */}
            <div style={{
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>🗂️ Shared Tasks</h3>
                <button
                  onClick={() => setShowTaskModal(true)}
                  data-testid="add-workspace-task-btn"
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  + Add Task
                </button>
              </div>

              {tasks.length === 0 ? (
                <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>
                  No tasks yet. Create a shared task for your workspace!
                </p>
              ) : (() => {
                const pending = tasks.filter(t => t.completion_percentage < 100);
                const completed = tasks.filter(t => t.completion_percentage >= 100);
                const pct = tasks.length ? Math.round((completed.length / tasks.length) * 100) : 0;
                return (
                  <div>
                    {/* Summary bar */}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '90px', padding: '12px', borderRadius: '12px', background: 'linear-gradient(135deg,#eef2ff,#e0e7ff)', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#4338ca' }}>{tasks.length}</div>
                        <div style={{ fontSize: '0.7rem', color: '#6366f1', fontWeight: 600 }}>📋 Total</div>
                      </div>
                      <div style={{ flex: 1, minWidth: '90px', padding: '12px', borderRadius: '12px', background: 'linear-gradient(135deg,#fff7ed,#ffedd5)', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#c2410c' }}>{pending.length}</div>
                        <div style={{ fontSize: '0.7rem', color: '#ea580c', fontWeight: 600 }}>⏳ Pending</div>
                      </div>
                      <div style={{ flex: 1, minWidth: '90px', padding: '12px', borderRadius: '12px', background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#15803d' }}>{completed.length}</div>
                        <div style={{ fontSize: '0.7rem', color: '#16a34a', fontWeight: 600 }}>✅ Done</div>
                      </div>
                    </div>

                    {/* Progress */}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#6b7280', marginBottom: '4px' }}>
                        <span>Group progress</span>
                        <span style={{ fontWeight: 700 }}>{pct}% {pct === 100 ? '🎊' : ''}</span>
                      </div>
                      <div style={{ height: '10px', backgroundColor: '#f1f5f9', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg,#667eea,#22c55e)', transition: 'width 0.4s' }} />
                      </div>
                    </div>

                    {pending.length > 0 && (
                      <div style={{ marginBottom: '14px' }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#ea580c', marginBottom: '8px' }}>⏳ TO DO ({pending.length})</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {pending.map(renderTaskCard)}
                        </div>
                      </div>
                    )}

                    {completed.length > 0 && (
                      <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#16a34a', marginBottom: '8px' }}>✅ COMPLETED ({completed.length})</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {completed.map(renderTaskCard)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Activity Feed */}

            <div style={{
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '12px' }}>📣 Recent Activity</h3>
              {activity.length === 0 ? (
                <p style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>No activity yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(showAllActivity ? activity : activity.slice(0, 5)).map(act => {
                    const icon = act.activity_type === 'task_completed' ? '✅'
                      : act.activity_type === 'task_created' ? '📝'
                      : act.activity_type === 'task_deleted' ? '🗑️'
                      : act.activity_type === 'task_reopened' ? '↩️'
                      : act.activity_type === 'member_invited' ? '✉️'
                      : act.activity_type === 'member_joined' ? '🎉'
                      : '•';
                    return (
                      <div key={act.id} style={{
                        padding: '8px 10px',
                        borderLeft: '3px solid #667eea',
                        backgroundColor: '#f9fafb',
                        borderRadius: '0 6px 6px 0',
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'flex-start'
                      }}>
                        <span style={{ fontSize: '0.95rem' }}>{icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.875rem' }}>{act.description}</div>
                          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                            {act.first_name || act.username} · {formatTime(act.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {activity.length > 5 && (
                    <button
                      onClick={() => setShowAllActivity(!showAllActivity)}
                      data-testid="toggle-activity-btn"
                      style={{
                        marginTop: '4px', padding: '8px', backgroundColor: '#f0f4ff', color: '#667eea',
                        border: '1px solid #c7d2fe', borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer'
                      }}
                    >
                      {showAllActivity ? '▲ Show less' : `▼ Show all ${activity.length} activities`}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f9fafb',
            borderRadius: '12px',
            minHeight: '400px'
          }}>
            <div style={{ textAlign: 'center', color: '#6b7280' }}>
              <p style={{ fontSize: '1.25rem', marginBottom: '8px' }}>👈 Select a workspace</p>
              <p>or create a new one to start collaborating</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Workspace Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '400px'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '16px' }}>Create Workspace</h2>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Name *</label>
              <input
                type="text"
                value={newWorkspace.name}
                onChange={e => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                placeholder="e.g., Family Tasks"
                data-testid="workspace-name-input"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Description</label>
              <input
                type="text"
                value={newWorkspace.description}
                onChange={e => setNewWorkspace({ ...newWorkspace, description: e.target.value })}
                placeholder="What's this workspace for?"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '8px' }}>Icon</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {iconOptions.map(icon => (
                  <button
                    key={icon}
                    onClick={() => setNewWorkspace({ ...newWorkspace, icon })}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      border: newWorkspace.icon === icon ? '2px solid #667eea' : '1px solid #e5e7eb',
                      backgroundColor: newWorkspace.icon === icon ? '#f0f4ff' : 'white',
                      fontSize: '1.25rem',
                      cursor: 'pointer'
                    }}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '8px' }}>Color</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {colorOptions.map(color => (
                  <button
                    key={color}
                    onClick={() => setNewWorkspace({ ...newWorkspace, color })}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: color,
                      border: newWorkspace.color === color ? '3px solid #111827' : '2px solid transparent',
                      cursor: 'pointer'
                    }}
                  />
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateWorkspace}
                disabled={!newWorkspace.name.trim()}
                data-testid="confirm-create-workspace-btn"
                style={{
                  padding: '10px 20px',
                  backgroundColor: newWorkspace.name.trim() ? '#667eea' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: newWorkspace.name.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Create Workspace
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '400px'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '16px' }}>Invite Family Member</h2>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Email Address *</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="family@example.com"
                data-testid="invite-email-input"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Role</label>
              <select
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              >
                <option value="member">Member - Can view and complete tasks</option>
                <option value="admin">Admin - Can also invite others</option>
              </select>
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '0.875rem',
              color: '#0369a1'
            }}>
              An invitation email will be sent to this address. If the person doesn&apos;t have an account, they&apos;ll be able to create one when they accept.
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleInviteMember}
                disabled={!inviteEmail.trim()}
                data-testid="confirm-invite-btn"
                style={{
                  padding: '10px 20px',
                  backgroundColor: inviteEmail.trim() ? '#22c55e' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: inviteEmail.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {showTaskModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '400px'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '16px' }}>Add Shared Task</h2>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Task Title *</label>
              <input
                type="text"
                value={newTask.title}
                onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="e.g., Buy groceries"
                data-testid="workspace-task-title-input"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Description</label>
              <textarea
                value={newTask.description}
                onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Add details..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Assign to</label>
              <select
                value={newTask.assignedTo}
                onChange={e => setNewTask({ ...newTask, assignedTo: e.target.value })}
                data-testid="workspace-task-assignee-select"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              >
                <option value="">Unassigned</option>
                {members.map(m => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.first_name || m.username} {m.last_name || ''}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '4px 0 0' }}>
                The assigned member gets an email notification.
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>Due Date</label>
              <input
                type="date"
                value={newTask.scheduledDate}
                onChange={e => setNewTask({ ...newTask, scheduledDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowTaskModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTask}
                disabled={!newTask.title.trim()}
                data-testid="confirm-add-task-btn"
                style={{
                  padding: '10px 20px',
                  backgroundColor: newTask.title.trim() ? '#667eea' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: newTask.title.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                Add Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaborativeWorkspaces;
